import { Component, type ReactNode } from "react";

interface SharePageErrorBoundaryProps {
  children: ReactNode;
}

interface SharePageErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary specifically for SharePage to catch any rendering errors
 * and display a fallback UI that still allows form access
 */
class SharePageErrorBoundary extends Component<
  SharePageErrorBoundaryProps,
  SharePageErrorBoundaryState
> {
  constructor(props: SharePageErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): SharePageErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Log error for debugging but don't break the user experience
    console.error("SharePage error boundary caught an error:", error, errorInfo);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      // Show a fallback UI that still allows users to try accessing the form
      return <SharePageErrorFallback error={this.state.error} />;
    }

    return this.props.children;
  }
}

/**
 * Fallback UI shown when SharePage encounters an error
 * Provides a way to retry or access the form directly
 */
function SharePageErrorFallback({ error }: { error: Error | null }): JSX.Element {
  // Get token from URL directly instead of useParams to avoid router dependency issues
  const token = typeof window !== "undefined" 
    ? window.location.pathname.match(/^\/share\/([^/]+)/)?.[1] 
    : null;

  const handleRetry = (): void => {
    window.location.reload();
  };

  return (
    <div className="share-page-container">
      <div className="share-page-content">
        <div className="surface" style={{ padding: "3rem 2rem", textAlign: "center" }}>
          <h1 style={{ margin: "0 0 1rem", fontSize: "2rem", fontWeight: 600 }}>
            Unable to Load Form
          </h1>
          <p style={{ color: "var(--color-text-muted)", fontSize: "1rem", lineHeight: 1.6, marginBottom: "2rem" }}>
            We encountered an error while loading the form. This might be a temporary issue.
          </p>
          <div style={{ display: "flex", gap: "1rem", justifyContent: "center", flexWrap: "wrap" }}>
            <button
              onClick={handleRetry}
              className="button primary"
              style={{
                padding: "0.75rem 1.5rem",
                fontSize: "1rem",
                fontWeight: 600,
              }}
            >
              Retry
            </button>
            {token && (
              <a
                href={`/share/${token}`}
                className="button"
                style={{
                  padding: "0.75rem 1.5rem",
                  fontSize: "1rem",
                  fontWeight: 600,
                  textDecoration: "none",
                  display: "inline-block",
                }}
              >
                Reload Page
              </a>
            )}
          </div>
          {process.env.NODE_ENV === "development" && error && (
            <details style={{ marginTop: "2rem", textAlign: "left", fontSize: "0.85rem" }}>
              <summary style={{ cursor: "pointer", color: "var(--color-text-muted)" }}>
                Error Details (Development Only)
              </summary>
              <pre
                style={{
                  marginTop: "0.5rem",
                  padding: "1rem",
                  background: "var(--color-bg)",
                  borderRadius: "8px",
                  overflow: "auto",
                  color: "var(--color-error, #c33)",
                }}
              >
                {error.toString()}
                {error.stack && `\n\n${error.stack}`}
              </pre>
            </details>
          )}
        </div>
      </div>
      <style>{`
        .share-page-container {
          min-height: 100vh;
          background: var(--color-bg, #f5f5f5);
          padding: 2rem 1rem;
          display: flex;
          justify-content: center;
          align-items: flex-start;
        }
        
        .share-page-content {
          width: 100%;
          max-width: 800px;
          margin: 0 auto;
        }
      `}</style>
    </div>
  );
}

export default SharePageErrorBoundary;
